import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

/** The query parameter the app adds for a logged in customer, see /api/booking/handoff. */
export const HANDOFF_PARAM = 'handoff';

const APP_CUSTOMER_KEY = 'pn-app-customer';

/**
 * Whether this tab came from the app with a login that worked. Only for what the page
 * shows: the customer's session is an httpOnly cookie, and the server decides.
 */
export function isAppCustomer(): boolean {
  try {
    return sessionStorage.getItem(APP_CUSTOMER_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Logs a customer who came from the app in for payments. The token is taken out of the
 * address at once, so it is not shared, bookmarked or reloaded; it works only once and
 * for two minutes anyway. A token that fails leaves the guest paying as a guest.
 */
export function useApiHandoff() {
  const router = useRouter();
  const exchanged = useRef<string | null>(null);
  const token = router.isReady ? router.query[HANDOFF_PARAM] : undefined;

  useEffect(() => {
    if (typeof token !== 'string' || !token || exchanged.current === token) return;
    exchanged.current = token;

    const { [HANDOFF_PARAM]: _token, ...query } = router.query;
    router.replace({ pathname: router.pathname, query }, undefined, {
      shallow: true,
      scroll: false,
    });

    fetch('/api/booking/handoff', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ token, locale: router.locale }),
    })
      .then((response) => {
        if (!response.ok) return;
        try {
          sessionStorage.setItem(APP_CUSTOMER_KEY, '1');
        } catch {
          // Only shows the receipt choice, which the server ignores for a guest anyway
        }
      })
      .catch(() => undefined);
  }, [token, router]);
}
